import {useMemo, useCallback} from "react"

import {useWalletQuery} from "@irati/wallet/hooks/wallet"
import authentication from "@irati/auth"
import {Deal, WalletEntityType, PaymentCurrencyType} from "@irati/types"
import {useOnce} from "@irati/utils/hooks/useOnce"

import redemptionUtil from "../utils/redemption"
import useStartCheckoutMutation from "./useStartCheckoutMutation"

interface CheckoutParams {
  useWallet: boolean
  deal: Deal
}

export interface CheckoutPayment {
  amount?: number
  walletAmount?: number
  currency?: string
  countryCode?: string
  paymentOrderId?: string
}

function useCheckout({useWallet, deal}: CheckoutParams) {
  const userId = authentication.user().uuid!
  const [startCheckoutMutation, {data: checkout, load: checkoutLoad}] = useStartCheckoutMutation({
    variables: {userId}
  })
  const {data: wallet, load: walletLoad} = useWalletQuery(
    {userId, currency: PaymentCurrencyType.Gbp, entityType: WalletEntityType.User},
    {fetchPolicy: "network-only"}
  )
  const load = useMemo(() => [walletLoad, checkoutLoad], [walletLoad, checkoutLoad])

  //create Checkout
  useOnce(startCheckoutMutation)

  // Updates payment details based on changes to checkout and useWallet
  const checkoutPayment: CheckoutPayment = useMemo(() => {
    if (!checkout?.paymentOrder?.amount) {
      return {}
    }

    let walletAmount = 0
    if (useWallet) {
      walletAmount = Math.min(checkout.paymentOrder.amount, wallet?.availableBalanceInCents || 0)
    }

    return {
      walletAmount,
      amount: checkout.paymentOrder.amount - walletAmount,
      currency: checkout.paymentOrder.currency,
      countryCode: checkout?.paymentOrder?.countryCode,
      paymentOrderId: checkout?.paymentOrder?.uuid
    }
  }, [checkout, wallet, useWallet])

  const onPaymentComplete = useCallback(
    () => redemptionUtil.paymentComplete(deal, checkout!.uuid!, checkout!.paymentOrder!.uuid!),
    [checkout, deal]
  )

  return {checkout, wallet, checkoutPayment, load, startCheckout: startCheckoutMutation, onPaymentComplete}
}

export default useCheckout
